import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { CheckIcon, PlusIcon, ShieldIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  T,
  RecordProps,
  TLShape,
  TLAsset,
  TLBinding,
  Editor,
  useEditor,
} from "tldraw";

import { isRootConjunct } from "@/components/canvas-editor/and-siblings";
import {
  BlockBody,
  BlockContainer,
  BlockContainerShapeContext,
  BlockSeparator,
  NodeBlockHeader,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasButton,
  CanvasInput,
  CanvasSimpleSelect,
} from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  Dimensions,
  inclusionOperatorOptions,
  inclusionOperators,
  InclusionOperator,
  LabelerLabelProps,
  LabelerLabelShape,
  labelerLabelType,
  labelSubjectOptions,
  labelSubjects,
  LabelSubject,
  metadataType,
} from "@/components/canvas-editor/shapes/shapes";
import {
  MIN_H,
  preventBubble,
  ToolStateNode,
  useIsReadonlyEditor,
} from "@/components/canvas-editor/utils";
import { Spinner } from "@/components/Spinner";
import { Avatar } from "@/components/ui/avatar";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { labelerQueryOptions, searchLabelersQueryOptions } from "@/lib/api";
import {
  isLookupableLabeler,
  LABEL_VALUE_PATTERN,
  LABELER_DID_PATTERN,
  LabelerDetail,
  labelerName,
  LabelerSummary,
  LabelValueOption,
  labelValueOptions,
  looksUnreachable,
  MAX_LABEL_VALUES,
  MISPLACED_POST_LABEL_MESSAGE,
  normalizeLabelerInput,
  retryingMessage,
  staleReadyNote,
  syncingMessage,
  unreachableMessage,
} from "@/lib/labeler-logic";
import { cn } from "@/lib/utils";

// The node itself is tinted (red for exclude, green for include), so a coloured line of
// text on it alone is low-contrast -- in dark mode especially, where step 11 on a dark red
// card reads as pink on maroon. Each message sits on a near-neutral callout of its own.
const errorCallout =
  "text-xs rounded-md px-2 py-1 border text-red-11 bg-red-2 border-red-7 dark:text-reddark-11 dark:bg-reddark-1 dark:border-reddark-7";
const noteCallout =
  "text-xs rounded-md px-2 py-1 border text-amber-11 bg-amber-2 border-amber-7 dark:text-amberdark-11 dark:bg-amberdark-1 dark:border-amberdark-7";

export function isLabelerLabelShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is LabelerLabelShape {
  return shape.type === labelerLabelType;
}

export class LabelerLabelTool extends ToolStateNode {
  static override id = labelerLabelType;

  static override uiToolDef = (editor: Editor) => ({
    id: labelerLabelType,
    icon: ShieldIcon,
    label: LabelerLabelShapeUtil.docs.title,
    defaultColor: LabelerLabelShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(labelerLabelType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={LabelerLabel}
        util={LabelerLabelShapeUtil}
        description={labelerLabelDocs}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: labelerLabelType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

const labelerLabelDocs = (
  <div className="flex flex-col gap-2">
    <p>
      Include or exclude posts using labels from a Bluesky labeler — a
      moderation service such as Skywatch that labels accounts and posts.
    </p>
    <p>
      <strong>Accounts</strong> matches posts whose author carries any of the
      chosen labels. <strong>Posts</strong> removes posts that are themselves
      labelled; it can only exclude, and only at the top level of the feed,
      because a labeler labels a post after we have already matched it.
    </p>
    <p>
      The first time a labeler is used we copy its labels, which can take from
      seconds to a couple of hours. The feed can be saved meanwhile, but not
      published.
    </p>
  </div>
);

export class LabelerLabelShapeUtil extends BindableUtil<LabelerLabelShape> {
  static override migrations = createMigrations(labelerLabelType);
  static override type = labelerLabelType;

  static override docs = {
    defaultColor: "red" as const,
    title: "Labeler Label",
    description:
      "Include or exclude posts using labels from a Bluesky labeler, on the author's account or on the post itself.",
  };

  override getType() {
    return labelerLabelType;
  }

  static override props: RecordProps<LabelerLabelShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    labelerDid: T.string,
    values: T.arrayOf(T.string),
    subject: T.literalEnum(...labelSubjects),
    operator: T.literalEnum(...inclusionOperators),
    didField: T.string.optional(),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 520,
      minW: 560,
      h: MIN_H,
      labelerDid: "",
      values: [],
      subject: "account" as const,
      // Exclude, not Include: what people reach for a labeler to do is keep something
      // OUT, and the serve-time stage only acts on excludes.
      operator: "not_in" as const,
      metadata: undefined,
    };
  }

  override component(shape: LabelerLabelShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <LabelerLabel
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

/**
 * The icon for a labeler AND for every one of its values.
 *
 * Label values carry no icon of their own, so the labeler's avatar stands in for all of
 * them (decided 10/20). Bluesky's own moderation service has no avatar, so the fallback
 * is the labeler's initial and, with no name to take one from, a shield.
 */
function LabelerIcon({
  labeler,
  size = "md",
  className,
}: {
  labeler: Pick<LabelerSummary, "avatar" | "displayName" | "handle"> | undefined;
  size?: "xs" | "sm" | "md";
  className?: string;
}) {
  const name = labeler?.displayName?.trim() || labeler?.handle || "";

  return (
    <Avatar
      src={labeler?.avatar || undefined}
      alt={name}
      size={size}
      className={cn(size === "xs" && "w-5 h-5 text-[10px]", className)}
      fallback={
        name ? (
          name[0]?.toUpperCase()
        ) : (
          <ShieldIcon className="w-3/5 h-3/5" aria-hidden />
        )
      }
    />
  );
}

function LabelerHeader({ labeler }: { labeler: LabelerDetail }) {
  return (
    <div className="flex gap-2 items-center min-w-0">
      <LabelerIcon labeler={labeler} />
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate">{labelerName(labeler)}</div>
        <div className="text-slate-dim text-sm truncate">
          @{labeler.handle}
        </div>
      </div>
      {typeof labeler.likeCount === "number" && (
        <SimpleTooltip message="Likes on Bluesky">
          <span className="text-xs rounded-md px-2 py-1 border border-slate-6 dark:border-slatedark-6 bg-slate-2 dark:bg-slatedark-2">
            {labeler.likeCount.toLocaleString()} likes
          </span>
        </SimpleTooltip>
      )}
    </div>
  );
}

function Chip({
  tone,
  children,
}: {
  tone: "red" | "amber" | "blue" | "slate";
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded px-1.5 py-px text-[11px] font-medium border whitespace-nowrap",
        // Each tone sets its dark variant explicitly: the Radix dark scale runs the
        // other way at the top steps, so a light class alone reads wrong in dark mode.
        tone === "red" &&
          "bg-red-3 text-red-11 border-red-6 dark:bg-reddark-3 dark:text-reddark-11 dark:border-reddark-6",
        tone === "amber" &&
          "bg-amber-3 text-amber-11 border-amber-6 dark:bg-amberdark-3 dark:text-amberdark-11 dark:border-amberdark-6",
        tone === "blue" &&
          "bg-blue-3 text-blue-11 border-blue-6 dark:bg-bluedark-3 dark:text-bluedark-11 dark:border-bluedark-6",
        tone === "slate" &&
          "bg-slate-3 text-slate-11 border-slate-6 dark:bg-slatedark-3 dark:text-slatedark-11 dark:border-slatedark-6",
      )}
    >
      {children}
    </span>
  );
}

function ValueChips({ option }: { option: LabelValueOption }) {
  return (
    <>
      {option.severity === "alert" && <Chip tone="red">Alert</Chip>}
      {option.severity === "inform" && <Chip tone="blue">Inform</Chip>}
      {option.blurs === "content" && <Chip tone="amber">Blurs content</Chip>}
      {option.blurs === "media" && <Chip tone="amber">Blurs media</Chip>}
      {option.adultOnly && <Chip tone="red">18+</Chip>}
      {option.source === "undeclared" && (
        <SimpleTooltip message="The labeler uses this value but publishes no description of it.">
          <span>
            <Chip tone="slate">Undeclared</Chip>
          </span>
        </SimpleTooltip>
      )}
      {option.source === "other" && (
        <SimpleTooltip message="Typed in by hand. The labeler doesn't list this value, so check the spelling.">
          <span>
            <Chip tone="slate">Other value</Chip>
          </span>
        </SimpleTooltip>
      )}
    </>
  );
}

function ValueSummary({
  option,
  labeler,
}: {
  option: LabelValueOption;
  labeler: LabelerDetail | undefined;
}) {
  return (
    <div className="flex gap-2 items-start min-w-0 flex-1">
      <LabelerIcon labeler={labeler} size="xs" className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex gap-1.5 items-center flex-wrap">
          <span className="font-medium">{option.name}</span>
          {option.name !== option.value && (
            <code className="text-[11px] text-slate-dim">{option.value}</code>
          )}
          <ValueChips option={option} />
        </div>
        {option.description !== option.value && (
          <div className="text-xs text-slate-dim line-clamp-2">
            {option.description}
          </div>
        )}
      </div>
    </div>
  );
}

/** Pick a labeler from the directory: most-liked first, searchable. */
function LabelerSearchButton({
  onSelect,
}: {
  onSelect: (labeler: LabelerSummary) => void;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const debouncedTerm = useDebounce(term, 300);
  const {
    data: labelers,
    isFetching,
    isError,
  } = useQuery({ ...searchLabelersQueryOptions(debouncedTerm), enabled: open });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <SimpleTooltip message="Find a labeler">
        <PopoverTrigger asChild>
          <CanvasButton size="icon" variant="canvas">
            <span className="sr-only">Find a labeler</span>
            <MagnifyingGlassIcon />
          </CanvasButton>
        </PopoverTrigger>
      </SimpleTooltip>
      <PopoverContent className="p-0 w-96" {...preventBubble}>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search labelers"
            value={term}
            onValueChange={setTerm}
          />
          <CommandList>
            {isError ? (
              <div className="px-3 py-4 text-sm text-red-11 dark:text-reddark-11">
                Couldn't search labelers just now. You can still paste a
                handle or DID.
              </div>
            ) : isFetching && !labelers ? (
              <div className="h-12 w-full flex items-center justify-center text-slate-dim">
                <Spinner />
              </div>
            ) : !labelers?.length ? (
              <div className="px-3 py-4 text-sm text-slate-dim">
                No labelers match “{debouncedTerm}”.
              </div>
            ) : (
              <CommandGroup>
                {labelers.map((labeler) => (
                  <CommandItem
                    key={labeler.did}
                    value={labeler.did}
                    onSelect={() => {
                      onSelect(labeler);
                      setOpen(false);
                    }}
                  >
                    <LabelerIcon labeler={labeler} size="sm" />
                    <div className="flex flex-col flex-1 min-w-0">
                      <div className="flex justify-between gap-2 items-center">
                        <span className="truncate font-medium">
                          {labelerName(labeler)}
                        </span>
                        {typeof labeler.likeCount === "number" && (
                          <span className="text-xs text-slate-dim shrink-0">
                            {labeler.likeCount.toLocaleString()} likes
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-dim truncate">
                        @{labeler.handle}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Choose any number of the labeler's values, or type one it doesn't declare. */
function LabelValuePicker({
  labeler,
  values,
  onChange,
}: {
  labeler: LabelerDetail | undefined;
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const options = labelValueOptions(labeler, values);
  const typed = term.trim().toLowerCase();
  const isFull = values.length >= MAX_LABEL_VALUES;
  const canAddTyped =
    typed.length > 0 &&
    LABEL_VALUE_PATTERN.test(typed) &&
    !options.some((option) => option.value === typed);

  const toggle = (value: string) => {
    if (values.includes(value)) {
      onChange(values.filter((v) => v !== value));
    } else if (!isFull) {
      onChange([...values, value]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <CanvasButton variant="canvas" size="sm" className="self-start">
          <PlusIcon />
          {values.length ? "Choose labels" : "Add labels"}
        </CanvasButton>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[28rem]" align="start" {...preventBubble}>
        <Command>
          <CommandInput
            placeholder="Filter labels, or type another value…"
            value={term}
            onValueChange={setTerm}
          />
          <CommandList className="max-h-80">
            {options.length === 0 && !typed && (
              <div className="px-3 py-4 text-sm text-slate-dim">
                This labeler doesn't list any labels. Type a value it uses
                to add it.
              </div>
            )}
            <CommandGroup>
              {options.map((option) => {
                const checked = values.includes(option.value);

                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    keywords={[option.name, option.description]}
                    disabled={!checked && isFull}
                    onSelect={() => toggle(option.value)}
                    className="items-start"
                  >
                    <span
                      className={cn(
                        "mt-1 w-4 h-4 shrink-0 rounded-sm border flex items-center justify-center",
                        checked
                          ? "bg-slate-12 border-slate-12 text-slate-1 dark:bg-slatedark-12 dark:border-slatedark-12 dark:text-slatedark-1"
                          : "border-slate-8 dark:border-slatedark-8",
                      )}
                      aria-hidden
                    >
                      {checked && <CheckIcon className="w-3 h-3" />}
                    </span>
                    <ValueSummary option={option} labeler={labeler} />
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {/* Its own group, force-mounted: cmdk hides a group whose items all
                filter out, which is exactly when a typed value needs offering. */}
            <CommandGroup forceMount>
              {canAddTyped && (
                <CommandItem
                  value={`__other__${typed}`}
                  forceMount
                  disabled={isFull}
                  onSelect={() => {
                    toggle(typed);
                    setTerm("");
                  }}
                >
                  <PlusIcon className="w-4 h-4" />
                  <span>
                    Other value… <code>{typed}</code>
                  </span>
                </CommandItem>
              )}
              {typed.length > 0 && !LABEL_VALUE_PATTERN.test(typed) && (
                <div className="px-3 py-2 text-xs text-slate-dim">
                  Label values are lowercase letters, digits and dashes
                  {" (optionally starting with !)"}.
                </div>
              )}
            </CommandGroup>
          </CommandList>
          {isFull && (
            <div className="px-3 py-2 text-xs text-amber-11 dark:text-amberdark-11 border-t border-slate-6 dark:border-slatedark-6">
              A node can hold at most {MAX_LABEL_VALUES} labels.
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function SyncStatus({ labeler }: { labeler: LabelerDetail }) {
  const { state, progress, error } = labeler.status ?? {};

  if (state === "ready") {
    // Synced once and currently failing: its labels are still served, so a note only.
    const note = staleReadyNote(labeler);
    return note ? (
      <div className={noteCallout} role="note">
        {note}.
      </div>
    ) : null;
  }

  if (state === "unreachable") {
    return (
      <div className={errorCallout} role="alert">
        {unreachableMessage(labeler)}
      </div>
    );
  }

  // Today's shape of a dead labeler (backfilling, 0%, an error, no head): not
  // "Syncing… 0%" forever.
  if (looksUnreachable(labeler.status)) {
    return (
      <div className={noteCallout} role="status">
        {retryingMessage(labeler)} Publishing waits until it syncs.
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className={errorCallout} role="alert">
        Couldn't sync {labelerName(labeler)}
        {error ? `: ${error}.` : "."} We'll keep retrying; the feed can't be
        published until it syncs.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1" role="status">
      <div className="flex justify-between gap-2 text-xs">
        <span className="font-medium">
          {state === "backfilling"
            ? syncingMessage(labeler)
            : `Starting to sync ${labelerName(labeler)}…`}
        </span>
        <span className="text-slate-dim">Publishing waits until it's done</span>
      </div>
      <Progress
        className="h-1.5"
        value={Math.round(Math.min(1, Math.max(0, progress ?? 0)) * 100)}
        aria-label={`Sync progress for ${labelerName(labeler)}`}
      />
    </div>
  );
}

/** Where the node sits decides whether the serve-time stage sees it. */
function PlacementNote({
  shape,
  isBound,
}: {
  shape: LabelerLabelShape;
  isBound: boolean;
}) {
  const editor = useEditor();

  // A node not wired into the feed isn't filtering anything yet.
  if (!isBound) return null;

  const isPost = shape.props.subject === "post";
  const isServeTimeAccount =
    !isPost && shape.props.operator === "not_in";
  if (!isPost && !isServeTimeAccount) return null;
  if (isRootConjunct(editor, shape)) return null;

  // A misplaced post label is an ERROR, not advice: feed-processor refuses the save and
  // rust-smasher the load (true at match time, it would make an `or` always true), and
  // the editor blocks Save on the same rule (`labelSaveBlockReason`). The account case is
  // legal and still matches -- it only loses the serve-time half -- so it stays a note.
  if (isPost) {
    return (
      <div className={errorCallout} role="alert">
        {MISPLACED_POST_LABEL_MESSAGE}
      </div>
    );
  }

  return (
    <div className={noteCallout} role="note">
      Applies when matching only; labels added later won't remove posts already
      in the feed.
    </div>
  );
}

function LabelerLabel({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: LabelerLabelShape;
  className?: string;
  updateShape: (
    shape: LabelerLabelShape,
    props: Partial<LabelerLabelProps>,
  ) => void;
}) {
  const queryClient = useQueryClient();
  const isReadonly = useIsReadonlyEditor() || shape.props.isReadOnly;
  const variant = shape.props.operator === "in" ? "green" : "red";
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  // Debounced so a handle being typed is not looked up -- and tracked -- per keystroke.
  const lookup = useDebounce(normalizeLabelerInput(shape.props.labelerDid), 400);
  const {
    data: labeler,
    isPending,
    isError,
  } = useQuery({
    ...labelerQueryOptions(lookup),
    enabled: isLookupableLabeler(lookup),
  });

  // A pasted handle is stored as the DID it resolves to, the way the list node rewrites
  // a handle URL: the manifest takes a DID, and a handle can change owners. Only when the
  // block is editable -- a read-only canvas must not quietly rewrite the feed it shows.
  const resolvedDid = labeler?.did;
  useEffect(() => {
    if (isReadonly || !resolvedDid || !labeler) return;
    if (resolvedDid === shape.props.labelerDid) return;
    if (normalizeLabelerInput(shape.props.labelerDid) !== lookup) return;
    // Seed the DID's own cache entry so the rewrite doesn't refetch (and re-track).
    queryClient.setQueryData(labelerQueryOptions(resolvedDid).queryKey, labeler);
    updateShape(shape, { labelerDid: resolvedDid });
  }, [
    isReadonly,
    resolvedDid,
    labeler,
    lookup,
    shape,
    updateShape,
    queryClient,
  ]);

  const selectedOptions = labelValueOptions(labeler, shape.props.values).filter(
    (option) => shape.props.values.includes(option.value),
  );
  // Kept in the order the author chose them, which is the order the manifest stores.
  selectedOptions.sort(
    (a, b) =>
      shape.props.values.indexOf(a.value) - shape.props.values.indexOf(b.value),
  );

  const hasLookup = isLookupableLabeler(lookup);
  const typedSomething = shape.props.labelerDid.trim().length > 0;

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={shape.props.metadata?.color || variant}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={LabelerLabelShapeUtil.docs}
        />
        <BlockBody>
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {!isReadonly && (
              <div className="flex gap-2">
                {shape.props.subject === "post" ? (
                  // Not a select: `post_label` has no include form, so there is
                  // nothing to choose. Shown so the node still reads as a sentence.
                  <SimpleTooltip message="Post labels can only exclude">
                    <div
                      className="w-28 shrink-0 h-10 px-3 flex items-center rounded-md border border-slate-6 dark:border-slatedark-6 bg-slate-1 dark:bg-slatedark-1 text-sm text-slate-dim"
                      aria-label="Include or exclude: Exclude"
                    >
                      Exclude
                    </div>
                  </SimpleTooltip>
                ) : (
                  <CanvasSimpleSelect
                    aria-label="Include or exclude"
                    className="w-28 shrink-0"
                    value={shape.props.operator}
                    onValueChange={(v) =>
                      updateShape(shape, { operator: v as InclusionOperator })
                    }
                    options={inclusionOperatorOptions}
                  />
                )}
                <CanvasSimpleSelect
                  aria-label="Labelled subject"
                  tooltip="Whether the label is on the post's author or on the post itself"
                  className="w-32 shrink-0"
                  value={shape.props.subject}
                  onValueChange={(v) => {
                    const subject = v as LabelSubject;
                    updateShape(
                      shape,
                      subject === "post"
                        ? { subject, operator: "not_in" }
                        : { subject },
                    );
                  }}
                  options={labelSubjectOptions}
                />
                <SimpleTooltip message="A labeler's handle or DID">
                  <CanvasInput
                    aria-label="Labeler handle or DID"
                    value={shape.props.labelerDid}
                    className="flex-1 min-w-0"
                    placeholder="Labeler handle or DID"
                    onValueChange={(value) =>
                      updateShape(shape, { labelerDid: value })
                    }
                  />
                </SimpleTooltip>
                <LabelerSearchButton
                  onSelect={(picked) =>
                    updateShape(shape, { labelerDid: picked.did })
                  }
                />
              </div>
            )}

            {labeler ? (
              <>
                {!isReadonly && <BlockSeparator />}
                <LabelerHeader labeler={labeler} />
                <SyncStatus labeler={labeler} />
              </>
            ) : !typedSomething ? (
              !isReadonly && (
                <div className="text-sm text-slate-dim">
                  Choose a labeler to filter by its labels.
                </div>
              )
            ) : hasLookup && isPending ? (
              <div className="flex justify-center items-center p-2 h-8">
                <Spinner delay={500} />
              </div>
            ) : hasLookup && isError ? (
              <div className={errorCallout}>
                Couldn't find a labeler at {lookup}. Check the handle or DID.
              </div>
            ) : (
              !LABELER_DID_PATTERN.test(shape.props.labelerDid) && (
                <div className="text-sm text-slate-dim">
                  Enter a labeler's handle (like skywatch.blue) or DID.
                </div>
              )
            )}

            {(labeler || shape.props.values.length > 0) && (
              <>
                <BlockSeparator />
                <div className="flex flex-col gap-1.5">
                  <div className="text-xs text-slate-dim">
                    {shape.props.subject === "post"
                      ? "Posts labelled with any of"
                      : "Authors labelled with any of"}
                  </div>
                  {selectedOptions.length === 0 && (
                    <div className="text-sm text-slate-dim">
                      No labels chosen yet.
                    </div>
                  )}
                  {selectedOptions.map((option) => (
                    <div key={option.value} className="flex gap-2 items-start">
                      <ValueSummary option={option} labeler={labeler} />
                      {!isReadonly && (
                        <CanvasButton
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 p-0 shrink-0"
                          aria-label={`Remove ${option.name}`}
                          onClick={() =>
                            updateShape(shape, {
                              values: shape.props.values.filter(
                                (v) => v !== option.value,
                              ),
                            })
                          }
                        >
                          <XIcon />
                        </CanvasButton>
                      )}
                    </div>
                  ))}
                  {!isReadonly && labeler && (
                    <LabelValuePicker
                      labeler={labeler}
                      values={shape.props.values}
                      onChange={(values) => updateShape(shape, { values })}
                    />
                  )}
                </div>
              </>
            )}

            <PlacementNote shape={shape} isBound={isBound} />
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}

